import { Button, Skeleton } from '@barfacil/barfacil-ui'
import { Column } from '@tanstack/react-table'
import { ChevronDown, ChevronUp } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

type TableHeadSortProps<T> = {
  column: Column<T>
  children: React.ReactNode
}

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
))
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('border-t border-t-gray-200 [&_tr]:border-b', className)}
    {...props}
  />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('bg-gray-900 font-medium text-gray-50', className)}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b transition-colors hover:bg-gray-50 aria-[checked=true]:bg-sky-25/50',
      className,
    )}
    {...props}
  />
))
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-8 bg-gray-100 px-4 text-left align-middle font-medium text-gray-500 [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableHeadSort = <T,>({ column, children }: TableHeadSortProps<T>) => {
  const sorted = column.getIsSorted()
  const isAsc = column.getIsSorted() === 'asc'

  return (
    <Button
      className="gap-x-1 !pl-0 text-left uppercase focus-visible:ring-offset-0"
      variant="ghost"
      onClick={() => column.toggleSorting(isAsc)}
    >
      {children}

      <span className={cn(!sorted && 'opacity-0')}>
        {isAsc ? (
          <ChevronDown className="translate-y-0.5" size={16} />
        ) : (
          <ChevronUp className="translate-y-0.5" size={16} />
        )}
      </span>
    </Button>
  )
}
TableHeadSort.displayName = 'TableHeadSort'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('p-4 align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-gray-500', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

const TableLoading = ({ rows, columns }: { rows: number; columns: number }) => {
  return (
    <>
      <div className="flex w-full border-t border-gray-200 bg-gray-100">
        {Array.from({ length: columns }).map((_, index) => (
          <div
            className="hidden w-full p-4 first-of-type:block sm:block"
            key={index}
          >
            <Skeleton className="h-6 w-full" />
          </div>
        ))}
      </div>

      {Array.from({ length: rows }).map((_, index) => (
        <div className="flex w-full border-t border-gray-200" key={index}>
          {Array.from({ length: columns }).map((_, index) => (
            <div
              className="hidden w-full p-4 first-of-type:block sm:block"
              key={index}
            >
              <Skeleton className="h-6 w-full" />
            </div>
          ))}
        </div>
      ))}
    </>
  )
}
TableLoading.displayName = 'TableLoading'

const TableAlert = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="border-t border-gray-200 py-12 text-center text-sm">
      {children}
    </div>
  )
}
TableAlert.displayName = 'TableAlert'

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableHeadSort,
  TableRow,
  TableCell,
  TableCaption,
  TableLoading,
  TableAlert,
}
